import { notify, useModal } from '@billy/design-system'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useBookkeepingBankAccount } from '@features/accounts/hooks/useBookkeepingBankAccount'
import { SaveBankAccountPayload, useSaveBankAccount } from '@features/accounts/hooks/useSaveBankAccount'
import { useBankAccount } from '@features/bankAccounts/hooks/useBankAccount'
import { Account } from '@modules-deprecated/app/accounts/types'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { NotificationKeys } from '../../../../../enums/notificationKeys'
import { useForm } from '../../../../../hooks'
import { EditManualBankAccountModalStates } from '../consts/editManualBankAccountModalStates'
import { EditManualBankAccountModalState } from '../enums/editManualBankAccountModalState'
import {
  EditManualBankAccountForm,
  EditManualBankAccountFormSchema,
  getDefaultValues,
  getValidationSchema,
} from '../utils/editManualBankAccountFormData'

interface UseEditManualBankAccountFormParams {
  id: string
  accountId: string | undefined
  bankAccountId: string | undefined
  state: EditManualBankAccountModalState
  onSuccess?: (accountId: string) => void
}

export const useEditManualBankAccountForm = ({
  id,
  accountId,
  bankAccountId,
  state,
  onSuccess,
}: UseEditManualBankAccountFormParams) => {
  const { t } = useTranslation()
  const { close: closeModal } = useModal(id)
  const { organization } = useUserOrganization()
  const { Form, FormItem, handleSubmit, reset } = useForm<EditManualBankAccountFormSchema>({
    defaultValues: useMemo(() => getDefaultValues(organization), [organization]),
    validationSchema: getValidationSchema(t),
  })
  const { bookkeepingBankAccount } = useBookkeepingBankAccount(accountId)
  const { bankAccount } = useBankAccount(bankAccountId ?? '')
  const { saveBankAccount, isProcessing } = useSaveBankAccount({
    onSuccess: (account: Account) => handleSaveSuccess(account),
    onError: () => handleSaveFailure(),
  })

  const { subHeaderTKey, saveButtonTKey } = EditManualBankAccountModalStates[state]

  const submitForm = useCallback(() => {
    handleSubmit((data: EditManualBankAccountForm) => {
      const payload: SaveBankAccountPayload = {
        id: accountId,
        name: data.accountName,
        description: data.description,
        currencyId: data.currencyId,
        bankAccountId,
        reconciliationStartDate: data.reconciliationStartDate,
      }
      saveBankAccount(payload)
    })()
  }, [handleSubmit, saveBankAccount, accountId, bankAccountId])

  const handleSaveSuccess = useCallback(
    (account: Account) => {
      notify({
        id: NotificationKeys.BankAccountEdit,
        message: accountId ? t('bank.account.modal.success.edit') : t('bank.account.modal.success.create'),
        variant: 'success',
        closable: false,
      })
      closeModal()
      onSuccess?.(account.id)
    },
    [onSuccess, closeModal, t, accountId],
  )

  const handleSaveFailure = useCallback(() => {
    notify({
      id: NotificationKeys.BankAccountEdit,
      message: accountId ? t('bank.account.modal.error.edit') : t('bank.account.modal.error.create'),
      variant: 'error',
    })
  }, [t, accountId])

  useEffect(() => {
    if (bookkeepingBankAccount && bankAccount) {
      reset({
        accountName: bookkeepingBankAccount.name,
        currencyId: bookkeepingBankAccount.currencyId,
        description: bookkeepingBankAccount.description,
        reconciliationStartDate: bankAccount?.reconciliationStartDate,
      })
    }
  }, [bookkeepingBankAccount, bankAccount, reset])

  return { Form, FormItem, closeModal, isProcessing, saveButtonTKey, subHeaderTKey, submitForm }
}

export type EditManualBankAccountFormApi = ReturnType<typeof useEditManualBankAccountForm>
